import React, { useEffect, useState } from "react"
import { StatusContentKey } from "models/Eligibility"
import { GaObserver } from "lib/GaObserver"
import { EventAction, EventCategory, EventLabel } from "models/GoogleAnalytics"
import { Checklist } from "components/molecules/Checklist"
import { GetContent } from "lib/GetContent"
import { Button, Trustpilot } from "components/atoms"
import { Card } from "components/atoms/Card"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { Loader, LoaderTheme } from "components/atoms/Loader"
import { useHistory } from "react-router-dom"
import { FullApplication, OpportunityStage } from "models/FullApplication"
import { Paperwork2, Mirror } from "components/Illustrations"
import { OfferAcceptedView } from "./OfferAcceptedView"
import { OfferAcceptedBookedView } from "./OfferAcceptedBookedView"
import { CoreView } from "./CoreView"
import { PrepareForAppointmentView } from "./PrepareForAppointmentView"
import {
  getExpertName,
  getSplitFactFindProgress,
  getSplitFfPostcode,
  getUpcomingBooking,
  hasCompletedSplitFactFind,
  isGreenAbcTls,
  isInSplitFfNextStepsVariant,
} from "lib/SplitFactFind"
import { useSplitFactFind } from "hooks/useSplitFactFind"
import { pickHandoffFields } from "lib/JourneyHandoff"
import { hasUploadedRequiredDocuments } from "lib/Document"
import { useStatusData } from "./hooks/useStatusData"
import { ApplicationSummaryEnhanced } from "models/ApplicationSummaryEnhanced"
import { AVATAR_CLOUDFRONT_URL } from "../../../Config"
import MojoMiniLogo from "url:assets/images/mojo-mini-logo.svg"
import {
  MojoServiceTypes,
  shouldShowSpecialistAdviseStatus,
  shouldShowSpecialistPaymentStatus,
} from "lib/GetClientConfig"
import { SpecialistStatusPage } from "pages/Application/Status/SpecialistStatusPage"
import {
  Container,
  ContentContainer,
  Text,
  TitleWithoutBottomGap,
  Title,
  Flex,
  BoldText,
  SubTitle,
  CardTitle,
  TextSingleLine,
  BlueTextLink,
  FullWidthContentContainer,
  ContentOuterContainer,
  WonGraphic,
  MirrorImage,
  SideWrapper,
  ChecklistContainer,
  ChecklistHeader,
  LoaderContainer,
  MiniLogo,
} from "./shared/StyledComponents"
import { PaymentStatusPage } from "./PaymentStatusPage"

interface StatusPageProps {
  serviceType: MojoServiceTypes
  friendlyId: string
  opportunityStage: OpportunityStage
  refreshApplications: (invalidateCache: boolean) => void
  salesforceApplication: ApplicationSummaryEnhanced
  /**
   * The full application. Its flags say whether the split fact find is already submitted, and it
   * has the property's postcode.
   */
  application?: FullApplication
}

export const Status = ({
  serviceType,
  friendlyId,
  refreshApplications,
  salesforceApplication,
  application,
  opportunityStage = null,
}: StatusPageProps) => {
  const [appointmentDate, setAppointmentDate] = useState<Date>(null)
  const history = useHistory()

  const lender = salesforceApplication?.lender ?? "your lender"

  const { fetchedStatus, isLoading } = useStatusData({
    applicationId: salesforceApplication.applicationId,
    opportunityStage,
  })

  useEffect(() => {
    LogGaEvent({
      action: EventAction.pageViewed,
      event_category: EventCategory.formInteraction,
      event_label: "MyMojo - Status",
    })
    LogGaEvent({
      action: EventAction.caseStatus,
      event_category: EventCategory.customEvent,
      event_label: opportunityStage,
    })
  }, [])

  useEffect(() => {
    if (!salesforceApplication) return
    if (salesforceApplication?.hasAppointments) {
      const { appointments = [] } = salesforceApplication
      if (appointments.length) {
        const [appointment] = appointments
        const { appointmentDate } = appointment
        const apptDate = new Date(appointmentDate)
        setAppointmentDate(apptDate)
      }
    }
  }, [salesforceApplication])

  const contentStage: StatusContentKey = {
    [OpportunityStage.mortgageready]: StatusContentKey.advise, // Made it through fact find
    [OpportunityStage.online]: StatusContentKey.advise, // Fact find and pre-appointment / appointment pending
    [OpportunityStage.coach]: StatusContentKey.advise, // Mortgage coach flow
    [OpportunityStage.quoted]: StatusContentKey.recommendation, // Had a recommentation from the ME
    [OpportunityStage.submitted]: StatusContentKey.application, // After fact find and recommendation has been submittd. The application is with the lender
    [OpportunityStage.offer]: StatusContentKey.offer, // Lender has come back with an offer for the customer to review
    [OpportunityStage.exchanged]: StatusContentKey.offer, // Exchanged contracts & documents through soliciors & the process can go through to completed
    [OpportunityStage.closedwon]: StatusContentKey.won, // We have completed and they have finished their mortgage process
    [OpportunityStage.dip]: StatusContentKey.dip, // CMT are on the lender portal and manually keying in salesforce
    [OpportunityStage.completionSet]: StatusContentKey.completionSet, // Completion date has been set
    [OpportunityStage.presub]: StatusContentKey.wip, // Pre-submission (it is dipped and waiting feeback from lender)
    [OpportunityStage.awaiting]: StatusContentKey.wip, // Awaiting to go over to the case management team
    [OpportunityStage.returned]: StatusContentKey.wip, // Returned to advisor, its been DIPed but lender has questions or application is delcined from lender
    [OpportunityStage.packaged]: StatusContentKey.dip, // Fully ME packaged, everything we need to DIP
    [OpportunityStage.closedlost]: StatusContentKey.lost, // We have lost the client, they are not mortgage ready or chosen a different lender
  }[opportunityStage]

  // Check for core conditions
  const isCoreScenario =
    contentStage === StatusContentKey.advise &&
    fetchedStatus &&
    fetchedStatus.amount === null &&
    fetchedStatus.stageName === "Online" &&
    (fetchedStatus.opportunityRecordType1 === "PurchaseOpportunity" ||
      fetchedStatus.opportunityRecordType1 === "RemortgageOpportunity") &&
    fetchedStatus.opportunityOwnerTextForEmail !== "Mojo Mortgages"

  // Check for offer accepted conditions
  const isOfferAcceptedScenario =
    contentStage === StatusContentKey.advise &&
    fetchedStatus &&
    fetchedStatus.mipIssuedToCustomer === true &&
    fetchedStatus.amount === null &&
    fetchedStatus.stageName === "Nurture" &&
    fetchedStatus.opportunityRecordType1 === "PurchaseOpportunity" &&
    fetchedStatus.opportunityOwnerTextForEmail !== "Mojo Mortgages"

  // Check for offer accepted booked conditions
  const isAppointmentInFuture = appointmentDate && new Date(appointmentDate) > new Date()
  const isOfferAcceptedBookedScenario =
    isOfferAcceptedScenario && salesforceApplication?.hasAppointments && isAppointmentInFuture

  const isBTLSpecialist = (page: string) =>
    page === StatusContentKey.advise &&
    shouldShowSpecialistAdviseStatus(serviceType, salesforceApplication)

  const isBTLSpecialistPayment = (page: string) =>
    page === StatusContentKey.recommendation &&
    shouldShowSpecialistPaymentStatus(serviceType, salesforceApplication)

  // Green A/B/C with an appointment still to come: the booking confirmation, with routes into the
  // split fact find and the documents. It takes over from the core view, which is what these
  // users would otherwise get once their expert has been assigned.
  const upcomingAppointment = getUpcomingBooking(
    salesforceApplication?.appointments,
    application?.appointments
  )
  // Only for the experiment's variant arm, which needs `/status`: until that is back, or if it
  // failed, we don't know the arm and this stays false, so the usual advise content is shown rather
  // than the new page being given to someone who may be in the control arm.
  const isPrepareForAppointmentScenario =
    contentStage === StatusContentKey.advise &&
    !isBTLSpecialist(StatusContentKey.advise) &&
    !isOfferAcceptedScenario &&
    isGreenAbcTls(salesforceApplication?.tls) &&
    !!upcomingAppointment &&
    isInSplitFfNextStepsVariant(fetchedStatus?.currentExperiments)
  // What Salesforce holds of the split fact find is a read of nine objects, so it is only made for
  // someone who is going to see the view (which already means `/status` has come back).
  const { splitFactFind, error: splitFactFindError } = useSplitFactFind(
    friendlyId,
    isPrepareForAppointmentScenario
  )
  // `undefined` until the full application and Salesforce's side have both come back, so the
  // profile step doesn't show as outstanding and then flip. A read that failed counts as back: the
  // application's flag then decides on its own.
  const splitFfSettled = !!splitFactFind || !!splitFactFindError
  const splitFfComplete = splitFfSettled
    ? hasCompletedSplitFactFind(getSplitFactFindProgress(splitFactFind), application)
    : undefined
  // From the application this page already has, so it needs nothing waiting for
  const documentsComplete = hasUploadedRequiredDocuments(application)

  const getPageContent = () => {
    if (isPrepareForAppointmentScenario) return StatusContentKey.prepareForAppointment
    if (isCoreScenario) return StatusContentKey.core
    if (isOfferAcceptedBookedScenario) return StatusContentKey.offerAcceptedBooked
    if (isOfferAcceptedScenario) return StatusContentKey.offerAccepted
    return contentStage
  }

  const pageContent = getPageContent()

  const content = GetContent({
    [StatusContentKey.advise]: isBTLSpecialist(StatusContentKey.advise) ? (
      <>
        <SpecialistStatusPage friendlyId={friendlyId} />
      </>
    ) : (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label={EventLabel.bookAnAppointment}
        >
          <Title>Your personal Mojo expert is ready to make your mortgage recommendation</Title>
        </GaObserver>
        <Text>
          Mortgages can be complicated things, and here at Mojo we want to get it perfect. Your
          expert wants to review and approve everything, and it's far easier for you if we have a
          quick call.
        </Text>
        <Flex>
          <div style={{ margin: "0 auto" }}>
            <Trustpilot />
          </div>
        </Flex>
      </ContentContainer>
    ),
    [StatusContentKey.successfulApplicaton]: (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Successful Application"
        >
          <TitleWithoutBottomGap>
            You mortgage application has been successful!
          </TitleWithoutBottomGap>
        </GaObserver>
        <Text>Congratulations. {lender} is prepared to offer you a mortgage. 🎉</Text>
        <BoldText>What happens next?</BoldText>
        <Text>
          You’ll be sent the details of the offer directly from the lender and will need to accept
          the offer through your Case Manager.{" "}
        </Text>
        <Text>
          Your Case Manager will now see the offer through to completion and keep you updated.{" "}
        </Text>
      </ContentContainer>
    ),
    [StatusContentKey.recommendation]: isBTLSpecialistPayment(StatusContentKey.recommendation) ? (
      <PaymentStatusPage friendlyId={friendlyId} fetchedStatus={fetchedStatus} />
    ) : (
      <ContentContainer>
        <RecommendationSteps
          friendlyId={friendlyId}
          fetchedStatus={fetchedStatus}
          deal={{ lender }}
        />
      </ContentContainer>
    ),
    [StatusContentKey.dip]: (
      <ContentContainer>
        <DIPView />
      </ContentContainer>
    ),
    [StatusContentKey.offer]: (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Offer received"
        >
          <Title>Application approved</Title>
        </GaObserver>

        <Text>Congratulations. {lender} is prepared to offer you a mortgage. 🎉</Text>
        <SubTitle>What happens next?</SubTitle>
        <Text>
          Your Case Manager will now work with you until your mortgage completes and they will keep
          you updated throughout the process.
        </Text>
      </ContentContainer>
    ),
    [StatusContentKey.application]: (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Submitted Application"
        >
          <Title>We've submitted your application.</Title>
        </GaObserver>

        <Text>
          All paperwork for your mortgage has been completed, packaged and sent to {lender}.
        </Text>
        <Text>
          It can take a week or two for {lender} to process your application but we'll be in touch
          the moment we hear anything.
        </Text>
        <Text>
          Sometimes lenders ask for extra paperwork too, but you will hear from your case manager if
          that happens.
        </Text>
      </ContentContainer>
    ),
    [StatusContentKey.won]: (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Complete"
        >
          <Title>Congratulations on the new mortgage!</Title>
        </GaObserver>
        <Text>We hope its all smooth sailing, but we are always here to help if you need us.</Text>
        <Text>
          We'll be back in touch in 6 months before your current deal ends so we can help you find
          your new one.
        </Text>
      </ContentContainer>
    ),
    [StatusContentKey.lost]: (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Closed case"
        >
          <Title>Had a change of heart?</Title>
        </GaObserver>

        <Text>
          If you'd like to pick up where you left off, drop us a call, your Mortgage Expert will be
          more than happy to help.
        </Text>

        <Card
          content={
            <>
              <MortgageExpertAvatar advisorEmail={fetchedStatus?.ownerEmailAddress} />
              <CardTitle>Contact your Mortgage Expert</CardTitle>
              <TextSingleLine>
                {fetchedStatus?.opportunityOwnerTextForEmail || "Mojo Mortgages"}
              </TextSingleLine>
              <BlueTextLink href="tel:0333 123 0012">0333 123 0012</BlueTextLink>
              <BlueTextLink href={`mailto:${fetchedStatus?.ownerEmailAddress}`}>
                {fetchedStatus?.ownerEmailAddress}
              </BlueTextLink>
            </>
          }
        />
      </ContentContainer>
    ),
    [StatusContentKey.completionSet]: (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Post Rec"
        >
          <Title>A completion date has been set</Title>
        </GaObserver>
        <Text>
          Great news! Your completion date has been set for{" "}
          {fetchedStatus?.setCompletionDateForEmail}.
        </Text>
        <SubTitle>What happens next?</SubTitle>
        <Text>
          There shouldn't be much left to do, but depending on your situation there might be a
          couple of things to keep an eye on.
        </Text>
        <Text>
          Staying with your existing lender, your lender will continue to keep you updated.
        </Text>
        <Text>
          Moving to a new lender, it may be worth a quick call to your solicitors to make sure they
          have all they need.
        </Text>
        <Text>
          If moving to a new home, it's worth checking in with your estate agent to find out when
          you can collect your keys.
        </Text>
      </ContentContainer>
    ),
    [StatusContentKey.postRec]: (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Post Rec"
        >
          <TitleWithoutBottomGap>Your mortgage application is almost ready</TitleWithoutBottomGap>
        </GaObserver>
        <Text>You’ve chosen your next deal and one step closer to mortgage joy.</Text>
        <BoldText>We need some documents</BoldText>
        <Text>
          To complete your application to {lender}, we need you to upload some additional documents.
        </Text>
        <Button
          onClick={() => {
            LogGaEvent({
              action: EventAction.buttonClicked,
              event_category: EventCategory.functionalInteraction,
              event_label: EventLabel.uploadDocs,
            })
            history.push(`/application/${friendlyId}/documents`)
          }}
          text={"Upload documents"}
        />
      </ContentContainer>
    ),
    [StatusContentKey.wip]: (
      <ContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Work in progress"
        >
          <Title>We're working on things... sit tight..</Title>
        </GaObserver>

        <Text>
          We're in the process of completing some work on your application, please contact us for
          further details or drop back in later and things should be updated
        </Text>
      </ContentContainer>
    ),
    [StatusContentKey.core]: (
      <FullWidthContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Core Status"
        >
          <CoreView fetchedStatus={fetchedStatus} friendlyId={friendlyId} />
        </GaObserver>
      </FullWidthContentContainer>
    ),
    // Waits for what says whether the profile is done, so it doesn't show as outstanding and then flip
    [StatusContentKey.prepareForAppointment]:
      !upcomingAppointment || splitFfComplete === undefined ? (
        <LoadingStateView />
      ) : (
        <FullWidthContentContainer>
          <GaObserver
            category={EventCategory.siteInteraction}
            action={EventAction.componentDisplay}
            label={
              splitFfComplete
                ? EventLabel.prepareForAppointmentDocsOnly
                : EventLabel.prepareForAppointment
            }
          >
            <PrepareForAppointmentView
              friendlyId={friendlyId}
              appointmentDate={new Date(upcomingAppointment.appointmentDate)}
              expertName={getExpertName(upcomingAppointment.assignee)}
              profileComplete={splitFfComplete}
              documentsComplete={documentsComplete}
              postcode={getSplitFfPostcode(application, salesforceApplication)}
              handoff={pickHandoffFields(application)}
            />
          </GaObserver>
        </FullWidthContentContainer>
      ),
    [StatusContentKey.offerAcceptedBooked]: (
      <FullWidthContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Offer Accepted Booked Status"
        >
          <OfferAcceptedBookedView
            fetchedStatus={fetchedStatus}
            friendlyId={friendlyId}
            appointmentDate={appointmentDate}
          />
        </GaObserver>
      </FullWidthContentContainer>
    ),
    [StatusContentKey.offerAccepted]: (
      <FullWidthContentContainer>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Offer Accepted Status"
        >
          <OfferAcceptedView fetchedStatus={fetchedStatus} friendlyId={friendlyId} />
        </GaObserver>
      </FullWidthContentContainer>
    ),
    default: (
      <ContentContainer>
        <LoadingStateView />
      </ContentContainer>
    ),
  })(pageContent)

  return (
    <Container data-testid="status-container">
      <ContentOuterContainer
        wider={
          pageContent === StatusContentKey.booked ||
          pageContent === StatusContentKey.offerAccepted ||
          pageContent === StatusContentKey.offerAcceptedBooked ||
          pageContent === StatusContentKey.core ||
          pageContent === StatusContentKey.prepareForAppointment
        }
      >
        {(!isLoading && content) || <LoadingStateView />}
      </ContentOuterContainer>

      {isBTLSpecialist(pageContent) ||
      isBTLSpecialistPayment(pageContent) ||
      pageContent === StatusContentKey.offerAccepted ||
      pageContent === StatusContentKey.offerAcceptedBooked ||
      pageContent === StatusContentKey.core ||
      pageContent === StatusContentKey.prepareForAppointment ? null : [
          StatusContentKey.won,
        ].includes(
          // No sidebar for offer accepted, offer accepted booked, core or prepare for appointment
          contentStage
        ) ? (
        <WonGraphic src={Paperwork2} alt="mirror" />
      ) : [StatusContentKey.wip, StatusContentKey.lost].includes(contentStage) ? (
        <div>
          <MirrorImage src={Mirror} alt="mirror" />
        </div>
      ) : (
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Case Stage"
        >
          <SideWrapper>
            <ChecklistContainer>
              <ChecklistHeader>Here’s where you’re at right now...</ChecklistHeader>
              <Checklist stage={contentStage} />
            </ChecklistContainer>
          </SideWrapper>
        </GaObserver>
      )}
    </Container>
  )
}

const RecommendationSteps = ({ fetchedStatus, deal, friendlyId }) => {
  const history = useHistory()
  if (fetchedStatus?.recommendationAccepted && !fetchedStatus.documentsRequested) {
    // Complete: Accepted and no documents required
    return <DIPView />
  }

  if (fetchedStatus?.recommendationAccepted && fetchedStatus.documentsRequested) {
    // Ready: Accepted and documents are required
    return (
      <>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Recommendation accepted, docs required"
        >
          <Title>Great news, you've accepted our mortgage recommendation!</Title>
        </GaObserver>
        <SubTitle>What’s next</SubTitle>
        <Text>
          To submit your application to {deal.lender}, we just need you to upload some additional
          documents
        </Text>
        <Button
          width="auto"
          onClick={() => {
            LogGaEvent({
              action: EventAction.buttonClicked,
              event_category: EventCategory.functionalInteraction,
              event_label: EventLabel.uploadDocs,
            })
            history.push(`/application/${friendlyId}/documents`)
          }}
          text={"Upload documents"}
        />
      </>
    )
  }

  if (fetchedStatus?.recommendationAccepted === false) {
    // Recommendation has been made to the client yet
    return (
      <>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Recommendation ready"
        >
          <Title>Your mortgage recommendation is ready</Title>
        </GaObserver>
        <SubTitle>What’s next</SubTitle>
        <Text>
          Your Mortgage Expert has reviewed your circumstances and preferences and has sourced their
          recommended deal for you to view.
        </Text>
        <Button
          onClick={() => {
            LogGaEvent({
              action: EventAction.buttonClicked,
              event_category: EventCategory.functionalInteraction,
              event_label: EventLabel.digiRec,
            })
            history.push(`/application/${friendlyId}/recommendation`)
          }}
          width="auto"
          text={"Review recommendation"}
        />
      </>
    )
  }

  if (fetchedStatus?.recommendationAccepted === null) {
    // Recommendation has not been made to the client yet
    return (
      <>
        <GaObserver
          category={EventCategory.siteInteraction}
          action={EventAction.componentDisplay}
          label="Recommendation unsent"
        >
          <Title>Your recommendation is on its way</Title>
        </GaObserver>
        <Text>
          Your Mortgage Expert will be in contact with you within the next working day to provide
          the official recommendation and begin the application process.
        </Text>
        {/* <Button width="auto" text="View selected deal" /> TODO: Wider discussion needed */}
      </>
    )
  }

  return (
    // Error state
    <>
      <GaObserver
        category={EventCategory.siteInteraction}
        action={EventAction.componentDisplay}
        label="Recommendation failure"
      >
        <Title>...</Title>
      </GaObserver>
      <LoadingStateView />
    </>
  )
}

const DIPView = () => {
  return (
    <>
      <GaObserver
        category={EventCategory.siteInteraction}
        action={EventAction.componentDisplay}
        label="Recommendation is ready"
      >
        <Title>Recommendation accepted</Title>
      </GaObserver>
      <Text>You’ve chosen your next mortgage deal and you’re now one step closer.</Text>
      <SubTitle>What happens next?</SubTitle>
      <Text>As mentioned on your call, there's no need to provide any additional documents.</Text>
      <Text>
        We have everything we need from you right now, we'll be in touch as soon as your application
        has been submitted
      </Text>
    </>
  )
}

const LoadingStateView = () => (
  <ContentContainer>
    <LoaderContainer>
      <Loader size={100} theme={LoaderTheme.Outline} />
    </LoaderContainer>
  </ContentContainer>
)

const checkImageURLisValid = (url: string) => {
  return new Promise<boolean>((resolve) => {
    const image = document.createElement("img")
    image.src = url
    image.onload = () => resolve(true)
    image.onerror = () => resolve(false)
  })
}

const MortgageExpertAvatar = ({ advisorEmail }: { advisorEmail: string }) => {
  const [adviserAvatar, setAdviserAvatar] = React.useState<string>(MojoMiniLogo)
  useEffect(() => {
    const req = `${AVATAR_CLOUDFRONT_URL}/${advisorEmail}.jpg`
    checkImageURLisValid(req).then((valid) => (valid ? setAdviserAvatar(req) : null))
  }, [setAdviserAvatar, advisorEmail])

  return <MiniLogo backgroundImage={adviserAvatar} borderRadius={adviserAvatar !== MojoMiniLogo} />
}
